import React from 'react';
import {
  CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { getMomentum, MomentumPayload } from '../../../services/cgBoostApi';
import { ViewDataBoundary } from '../useViewData';
import { cardStyle, eyebrowLabel, flagToStatus, sectionTitle } from '../styles';
import { StatusPill } from '../../ui';

interface Props { campaignId: string; }

const MomentumView: React.FC<Props> = ({ campaignId }) => (
  <ViewDataBoundary
    loader={getMomentum}
    campaignId={campaignId}
    loadingLabel="Loading momentum…"
    render={(data) => <MomentumBody data={data} />}
  />
);

const MomentumBody: React.FC<{ data: MomentumPayload }> = ({ data }) => {
  const weekly = data.weekly;

  return (
    <>
      <div style={cardStyle}>
        <div style={sectionTitle}>Did the campaign improve or decline over time?</div>
        <ChartCard title="Reach · Audience Exposure · Spend (Week-over-Week)">
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={weekly}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis dataKey="weekLabel" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={(v) => formatCompact(v)} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${formatCompact(v)}`} />
              <Tooltip formatter={(v: any) => (typeof v === 'number' ? v.toLocaleString() : v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line yAxisId="left" type="monotone" dataKey="impressions" name="Reach" stroke="var(--accent)" strokeWidth={2} />
              <Line yAxisId="left" type="monotone" dataKey="views" name="Audience Exposure" stroke="var(--success)" strokeWidth={2} />
              <Line yAxisId="right" type="monotone" dataKey="spend" name="Spend" stroke="var(--viz-2)" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 16 }}>
          <ChartCard title="Audience Attention (VTR) per Week">
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={weekly}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="weekLabel" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v * 100).toFixed(0)}%`} domain={['dataMin - 0.05', 'dataMax + 0.05']} />
                <Tooltip formatter={(v: any) => (typeof v === 'number' ? `${(v * 100).toFixed(1)}%` : v)} />
                <Line type="monotone" dataKey="vtr" name="VTR" stroke="var(--viz-4)" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title="Ad Frequency per Week">
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={weekly}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="weekLabel" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v: any) => (typeof v === 'number' ? v.toFixed(2) : v)} />
                <Line type="monotone" dataKey="frequency" name="Frequency" stroke="var(--warning)" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={sectionTitle}>Optimization Notes</div>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--fg-1)' }}>
          {data.optimizationNotes.map((n, i) => (
            <li key={i} style={{ marginBottom: 6 }}>
              <strong style={{ marginRight: 8, color: 'var(--fg-2)' }}>{n.date}</strong>
              {n.note}
            </li>
          ))}
        </ul>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
        <FlagAlert
          title="Fatigue Detected"
          triggered={data.flags.fatigueDetected}
          plainLanguage={data.flags.plainLanguage.fatigue}
        />
        <FlagAlert
          title="Momentum Maintained"
          triggered={data.flags.momentumMaintained}
          plainLanguage={data.flags.plainLanguage.momentum}
          variant="info"
        />
        <FlagAlert
          title="Refresh Window"
          triggered={data.flags.refreshWindow}
          plainLanguage={data.flags.plainLanguage.refresh}
          variant="info"
        />
        <FlagAlert
          title="Frequency Warning"
          triggered={data.flags.frequencyWarning}
          plainLanguage={data.flags.plainLanguage.frequency}
        />
      </div>
    </>
  );
};

const ChartCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ ...eyebrowLabel, fontSize: 12, marginBottom: 8 }}>{title}</div>
    {children}
  </div>
);

const FlagAlert: React.FC<{
  title: string;
  triggered: boolean;
  plainLanguage: string;
  variant?: 'warn' | 'info';
}> = ({ title, triggered, plainLanguage, variant = 'warn' }) => (
  <div style={{
    ...cardStyle,
    borderColor: triggered ? (variant === 'warn' ? 'rgba(234,88,12,0.45)' : 'rgba(59,130,246,0.45)') : 'var(--border-1)',
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
      <div style={{ fontSize: 14, fontWeight: 600 }}>{title}</div>
      <StatusPill
        variant={flagToStatus[triggered ? (variant === 'warn' ? 'warn' : 'info') : 'muted']}
        noDot
        size="sm"
      >
        {triggered ? 'Triggered' : 'OK'}
      </StatusPill>
    </div>
    <div style={{ fontSize: 13, color: 'var(--fg-2)' }}>{plainLanguage}</div>
  </div>
);

function formatCompact(v: number): string {
  if (Math.abs(v) >= 1_000_000) return (v / 1_000_000).toFixed(1) + 'M';
  if (Math.abs(v) >= 1_000) return (v / 1_000).toFixed(1) + 'k';
  return String(v);
}

export default MomentumView;
